/**
 * FECHAS DEL CHAT — separadores por día y hora en la burbuja, como WhatsApp.
 *
 * (25/08/2026, pedido del user) El hilo de Gustavo es UNO SOLO por agente y no se cierra
 * nunca: adentro conviven lo de esta mañana y lo de hace tres meses, y hasta ahora no había
 * ninguna marca de cuándo se dijo cada cosa. Sin eso, al scrollear hacia arriba no hay forma
 * de saber si lo que se está leyendo es de ayer o de marzo.
 *
 * Todo lo de acá es PURO —recibe la fecha de referencia por parámetro— para poder probar el
 * cambio de día sin depender del reloj de quien corre los tests.
 */

/** La hora que va abajo de la burbuja. 24 h: es como se lee la hora en Uruguay. */
export function horaCorta(fecha: Date): string {
  return fecha.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** El día calendario de una fecha, para comparar dos fechas sin la hora. */
function inicioDelDia(f: Date): number {
  return new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime();
}

/** Cuántos días calendario separan a `fecha` de `hoy` (0 = mismo día, 1 = ayer). */
export function diasDeDiferencia(fecha: Date, hoy: Date): number {
  return Math.round((inicioDelDia(hoy) - inicioDelDia(fecha)) / 86400000);
}

/**
 * El texto del separador de día.
 *
 * Los dos días recientes van por su nombre —"Hoy", "Ayer"— porque es como se los nombra al
 * hablar. Dentro de la semana alcanza el día de la semana; más atrás hace falta la fecha, y
 * cambiando de año, también el año: "14 de agosto" a secas, leído en enero, no dice cuál.
 */
export function etiquetaDeDia(fecha: Date, hoy: Date = new Date()): string {
  const dias = diasDeDiferencia(fecha, hoy);
  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Ayer';
  if (dias > 1 && dias < 7) {
    const dia = fecha.toLocaleDateString('es-UY', { weekday: 'long' });
    return dia.charAt(0).toUpperCase() + dia.slice(1);
  }
  const mismoAnio = fecha.getFullYear() === hoy.getFullYear();
  return fecha.toLocaleDateString('es-UY', {
    day: 'numeric', month: 'long', ...(mismoAnio ? {} : { year: 'numeric' }),
  });
}

/**
 * ⛔ Una fecha FUTURA no es "hace -1 días".
 *
 * Un reloj del cliente atrasado deja mensajes recién enviados con hora de mañana; con la
 * cuenta cruda salía "Ayer" al revés o un día de la semana suelto. Se los trata como de hoy,
 * que es cuando el agente los está mirando.
 */
export function etiquetaSegura(fecha: Date, hoy: Date = new Date()): string {
  return diasDeDiferencia(fecha, hoy) < 0 ? 'Hoy' : etiquetaDeDia(fecha, hoy);
}

/** Clave de agrupación: mismo día calendario = misma clave. */
export function claveDeDia(fecha: Date): string {
  return `${fecha.getFullYear()}-${fecha.getMonth() + 1}-${fecha.getDate()}`;
}

export type ItemDeChat<T> =
  | { tipo: 'dia'; clave: string; etiqueta: string }
  | { tipo: 'mensaje'; clave: string; mensaje: T };

/**
 * Intercala un separador cada vez que cambia el día, en el orden en que ya vienen los
 * mensajes (del más viejo al más nuevo).
 *
 * Devuelve una lista plana y no un array de grupos a propósito: el chat los dibuja uno atrás
 * de otro dentro del mismo `Stack`, y anidando grupos el espaciado entre burbujas de días
 * distintos deja de ser el mismo que el de adentro del día.
 */
export function conSeparadoresDeDia<T extends { id: string; createdAt: Date }>(
  mensajes: T[],
  hoy: Date = new Date(),
): ItemDeChat<T>[] {
  const items: ItemDeChat<T>[] = [];
  let ultimaClave: string | null = null;
  for (const m of mensajes) {
    const fecha = m.createdAt instanceof Date ? m.createdAt : new Date(m.createdAt);
    // Una fecha inválida (mensaje viejo sin `createdAt`) no puede cortar el hilo en dos ni
    // pintar un separador con "Invalid Date": se lo deja bajo el día que venía corriendo.
    if (!Number.isNaN(fecha.getTime())) {
      const clave = claveDeDia(fecha);
      if (clave !== ultimaClave) {
        items.push({ tipo: 'dia', clave: `dia-${clave}`, etiqueta: etiquetaSegura(fecha, hoy) });
        ultimaClave = clave;
      }
    }
    items.push({ tipo: 'mensaje', clave: m.id, mensaje: m });
  }
  return items;
}
